// src/managerPortal/pages/ManagerDashboard.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import SidebarMenu from "../components/SidebarMenu";
import "../styles/ManagerDashboard.css";

// Chart.js
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

// Import our dynamic API URL
import apiUrl from "../../config"; // Adjust the path if your config.js is elsewhere

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const ManagerDashboard = () => {
    const [menuItems, setMenuItems] = useState([]);
    const [events, setEvents] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const navigate = useNavigate();

    // (Optional) Logout logic
    const handleLogout = async () => {
        try {
            await axios.post(`${apiUrl}/api/auth/logout`, {}, { withCredentials: true });
            navigate("/manager/signin");
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    // Fetch data on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const menuRes = await axios.get(`${apiUrl}/api/manager/menu`, {
                    withCredentials: true,
                });
                setMenuItems(menuRes.data);

                const eventsRes = await axios.get(`${apiUrl}/api/manager/events`, {
                    withCredentials: true,
                });
                setEvents(eventsRes.data);

                const reservationsRes = await axios.get(`${apiUrl}/api/manager/reservations`, {
                    withCredentials: true,
                });
                setReservations(reservationsRes.data);

            } catch (error) {
                // If the server returns 403, we can redirect to /manager/no-access
                if (error.response && error.response.status === 403) {
                    navigate("/manager/no-access");
                } else {
                    console.error("Error fetching data:", error);
                }
            }
        };
        fetchData();
    }, [navigate]);

    // Build chart data
    useEffect(() => {
        if (!reservations.length) return;

        const dateCounts = {};
        reservations.forEach((res) => {
            const dateKey = new Date(res.date).toLocaleDateString("en-CA");
            dateCounts[dateKey] = (dateCounts[dateKey] || 0) + 1;
        });

        const sortedDates = Object.keys(dateCounts).sort((a, b) => new Date(a) - new Date(b));
        const dataPoints = sortedDates.map((date) => dateCounts[date]);

        setChartData({
            labels: sortedDates,
            datasets: [
                {
                    label: "Reservations",
                    data: dataPoints,
                    backgroundColor: "rgba(135, 206, 235, 0.2)",
                    borderColor: "rgba(135, 206, 235, 1)",
                    borderWidth: 2,
                    fill: true,
                    tension: 0.3,
                },
            ],
        });
    }, [reservations]);

    // Helper to get latest 3
    const latest = (arr) => arr.slice(0, 3);

    return (
        <div className="layout">
            <SidebarMenu />
            <div className="main-content dashboard-content">
                <div className="dashboard-header">
                    <h2 className="dashboard-title">Manager Dashboard</h2>
                    {/* Optional Logout Button (if you want to use it)
                    <button onClick={handleLogout}>Logout</button>
                    */}
                </div>

                {/* Stats */}
                <div className="dashboard-stats">
                    <div className="stats-card card-animate">
                        <h3>Total Menu Items</h3>
                        <p className="stats-value">{menuItems.length}</p>
                    </div>
                    <div className="stats-card card-animate">
                        <h3>Total Events</h3>
                        <p className="stats-value">{events.length}</p>
                    </div>
                    <div className="stats-card card-animate">
                        <h3>Total Reservations</h3>
                        <p className="stats-value">{reservations.length}</p>
                    </div>
                </div>

                {/* Chart */}
                <div className="chart-section card-animate">
                    <div className="chart-header">
                        <h3>Reservations Over Time</h3>
                    </div>
                    {chartData.labels.length ? (
                        <Line data={chartData} />
                    ) : (
                        <p>No reservation data available.</p>
                    )}
                </div>

                {/* Latest Items */}
                <div className="latest-items-row">
                    <DashboardCard
                        title="Latest Menus"
                        items={latest(menuItems)}
                        link="/manager/menu"
                        keyName="name"
                        subKey="category"
                    />
                    <DashboardCard
                        title="Latest Events"
                        items={latest(events)}
                        link="/manager/event"
                        keyName="title"
                        subKey="date"
                    />
                    <DashboardCard
                        title="Latest Reservations"
                        items={latest(reservations)}
                        link="/manager/reservations"
                        keyName="name"
                        subKey="guests"
                    />
                </div>
            </div>
        </div>
    );
};

// Reusable sub-component
const DashboardCard = ({ title, items, link, keyName, subKey }) => (
    <div className="dashboard-card latest-card card-animate">
        <div className="dashboard-card-header">
            <h3>{title}</h3>
            <Link to={link} className="view-all-link">View All</Link>
        </div>
        <div className="dashboard-card-body">
            {items.length ? (
                <ul className="dashboard-list">
                    {items.map((item) => (
                        <li key={item._id}>
                            <strong>{item[keyName]}</strong> – {item[subKey]}
                        </li>
                    ))}
                </ul>
            ) : (
                <p>No data available.</p>
            )}
        </div>
    </div>
);

export default ManagerDashboard;
